<template lang="pug">
.stickscroll( :class="[stick ? 'sticky' : 'dont-stick']")
  .stickscroll-contents(ref="contentRef")
    slot
</template>
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { useMQ } from '../../js/mq'

function checkSupportsPassive() {
  let supportsPassive = false

  // Detect if the browser supports passive event listeners, which are more performant on scrolling
  if (typeof window !== 'undefined') {
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          supportsPassive = true

          return true
        },
      })
      window.addEventListener('testPassive', () => {}, opts)
      window.removeEventListener('testPassive', () => {}, opts)
    } catch {
      //
    }
  }
  
  return supportsPassive
}

const mq = useMQ()

const transform = ref(0)
const stick = ref(false)
const passiveSupported = ref(false)
const contentRef = ref<HTMLElement>()

let scrollListener: (() => void) | null = null
let resizeListener: (() => void) | null = null

function removeScrollListener() {
  if (scrollListener) {
    window.removeEventListener('scroll', scrollListener)
  }
  if (contentRef.value) {
    transform.value = 0
    contentRef.value.setAttribute('style', 'top: 0px')
  }
}

function check() {
  const prevVal = stick.value
  stick.value = mq.value !== 'mobile'
  if (prevVal !== stick.value) {
    if (stick.value) {
      window.addEventListener('scroll', scrollListener!, passiveSupported.value ? { passive: true } : false)
    } else {
      removeScrollListener()
    }
    if (scrollListener) {
      scrollListener()
    }
  }
}

useResizeObserver(contentRef, check)

onMounted(() => {
  passiveSupported.value = checkSupportsPassive()

  let lastScroll = window.scrollY

  let frame

  const checkScroll = () => {
    window.cancelAnimationFrame(frame)
    const content = contentRef.value
    const prevVal = transform.value
    if (!content) {
      return
    }

    frame = window.requestAnimationFrame(() => {
      if (content.clientHeight < window.innerHeight) {
        transform.value = 0
      } else {
        const delta = window.scrollY - lastScroll
        lastScroll = window.scrollY

        const contentRect = content.getBoundingClientRect()
        const parentRect = content.parentElement!.getBoundingClientRect()

        const isStickingTop = parentRect.top < 0
        const isStickingBottom = parentRect.bottom < window.innerHeight
        const maxScroll = contentRect.height - window.innerHeight

        if (!isStickingTop) {
          // If the top is not even sticking yet, we definitely dont want to scroll the content
          transform.value = 0
        } else if (isStickingBottom && delta > 0) {
          // If the bottom is stuck, it means the content is going to scroll with the page - so we dont have to transform it anymore
          transform.value = -1 * maxScroll
        } else {
          // We are sticking top
          transform.value = Math.max(-1 * maxScroll, Math.min(0, transform.value - delta))
        }
      }

      if (transform.value !== prevVal) {
        content.setAttribute('style', `top: ${transform.value}px`)
      }
    })
  }

  let resizeDebounce: NodeJS.Timeout | undefined

  resizeListener = () => {
    clearTimeout(resizeDebounce)
    resizeDebounce = setTimeout(() => {
      const prevVal = stick.value
      check()
      if (stick.value && prevVal && scrollListener) {
        scrollListener()
      }
    }, 25)
  }

  // If the content.is stuck, and has overflow, we want to scroll it proactively with the page
  scrollListener = checkScroll
  window.addEventListener('resize', resizeListener)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeListener!)
  removeScrollListener()
})
</script>
<style lang="scss" scoped>
.stickscroll {
  z-index: 1;
}

@include not-mobile() {
  .sticky {
    .stickscroll-contents {
      position: sticky;
      position: -webkit-sticky;
      top: $space-s;

      :slotted(.rel) {
        position: relative;
      }

      :slotted(.pos) {
        position: absolute;
      }
    }
  }
}
</style>
